import { graphqlClient, TGraphQLSubscriptions } from '../graphql-api-client/graphql-api-client';
import {
  CreatePollMutation,
  DeletePollMutation,
  ListPollChoicesQuery,
  ListPollsQuery,
  PollCommandMutation,
  UpdatePollAndPollChoicesMutation,
  UpdatePollMutation
} from '../../graphql/API';
import * as queries from '../../graphql/queries';
import * as mutations from '../../graphql/mutations';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { IPollEntity, IPollVotesResponse, IVoteCount } from '../../store/polls-store/poll-entity';
import ResponseEntity, { IResponseItem } from '../../store/polls-store/response-entity';
import { PollCommands } from '../../constans/polls-constants';
import { protectedContentApiClient } from '../api-fetcher/api-client';

export interface IRequestPoll {
  pollChoices: IResponseItem[];
  poll: Partial<IPollInfo>;
  presentationId: string | null;
}

export interface IPollInfo extends Partial<IPollEntity> {
  presentationId: string;
  order: number;
}

export interface ICreatePollResponse {
  Polls: IPollEntity[];
  PollChoices: ResponseEntity[];
}

export interface ISavePollVotesResponse {
  allVotesCount: number;
  pollId: string;
  presentationId: string;
  pollVotesCount: IVoteCount[];
}

export interface ICommandResponse {
  poll_choices: ResponseEntity[];
  poll_id: string;
  presentation_id: string;
  status: string;
  type: string;
  value: string;
  public_adjustment: number;
  poll_status_timestamp?: string;
}

export interface ISubscriptionsData {
  onDeletePoll: ICreatePollResponse;
  onSavePollVotes: ISavePollVotesResponse;
  onCreatePoll: ICreatePollResponse;
  onUpdatePoll: IPollEntity;
}

export interface IPollSlideGenerateRequest {
  presentation_id: number | null;
  status: string;
  type: string;
  name: string;
  poll_id: string;
  poll_title: string;
  poll_votes: IPollVotesResponse[];
}

interface IDimensions {
  width: number;
  height: number;
  aspect_ratio: string;
}

export interface IPollSlideGenerateResponse {
  animation_splitted: boolean | null;
  category_id: number | null;
  conversion_state: string;
  description: string | null;
  dimensions: IDimensions | null;
  id: number;
  license_id: number;
  modified_date: string;
  name: string;
  parent_id: number | null;
  processing_state: string;
  related_assets: Array<IRelatedAsset>;
  source_url: string;
  state: string;
  subaccount_id: number;
  thumbnail_url: string;
  title: string;
  type: string;
  upload_date: string;
  user_id: number;
  user_name: string;
}

interface IRelatedAsset {
  metadata: IRelatedAssetMetadata;
}

interface IRelatedAssetMetadata {
  thumbnail_url: string;
}

export type TSubscription<T> = (
  query: string,
  presentationId: string | null,
  doneCallback: (data: T) => void,
  retrySubscriptionFn: () => TGraphQLSubscriptions
) => TGraphQLSubscriptions;

export const generatePollResultsSlideApi = (body: IPollSlideGenerateRequest): Promise<IPollSlideGenerateResponse> => {
  return protectedContentApiClient.post<IPollSlideGenerateResponse, IPollSlideGenerateRequest>('slides/register/poll', { ...body });
};

export const getPollsListQuery = async (presentationId: string): Promise<GraphQLResult<ListPollsQuery>> => {
  return graphqlClient.operation<ListPollsQuery>({
    query: queries.listPolls,
    variables: {
      presentationId: presentationId,
    }
  });
};

export const getPollsChoicesQuery = async (pollId: string): Promise<GraphQLResult<ListPollChoicesQuery>> => {
  return graphqlClient.operation<ListPollChoicesQuery>({
    query: queries.listPollChoices,
    variables: {
      pollId: pollId,
    }
  });
};

export const createPollMutation = async (body: IRequestPoll): Promise<void> => {
  await graphqlClient.operation<CreatePollMutation>({
    query: mutations.createPoll,
    variables: {
      ...body
    }
  });
};

export const updatePollMutation = async (body: Partial<IPollEntity>, presentationId: string): Promise<void> => {
  await graphqlClient.operation<UpdatePollMutation>({
    query: mutations.updatePoll,
    variables: {
      poll: {
        ...body
      },
      presentationId: presentationId,
    }
  });
};

export const pollCommandMutation = async (
  body: Partial<IPollEntity>,
  presentationId: string | null,
  livePresentationId: number | undefined,
  command: PollCommands,
  isVod: boolean
): Promise<GraphQLResult<PollCommandMutation>> => {
  return graphqlClient.operation<PollCommandMutation>({
    query: mutations.pollCommand,
    variables: {
      poll: {
        ...body
      },
      presentationId: presentationId,
      livePresentationId: livePresentationId,
      pollCommand: command,
      isVod: isVod
    }
  });
};

export const deletePollMutation = async (deletedId: string | null, presentationId: string | null): Promise<void> => {
  await graphqlClient.operation<DeletePollMutation>({
    query: mutations.deletePoll,
    variables: {
      pollId: deletedId,
      presentationId: presentationId
    }
  });
};

export const updatePollAndChoicesMutation = async (presentationId: string | null, poll: Partial<IPollEntity>, pollChoices: IResponseItem[]): Promise<void> => {
  await graphqlClient.operation<UpdatePollAndPollChoicesMutation>({
    query: mutations.updatePollAndPollChoices,
    variables: {
      poll: poll,
      pollChoices: pollChoices,
      presentationId: presentationId
    }
  });
};

export const pollSubscription: TSubscription<ISubscriptionsData> = (query, presentationId, doneCallback, retrySubscriptionFn): TGraphQLSubscriptions => {
  return graphqlClient.subscriptions({
    query: query,
    variables: {
      presentationId: presentationId,
    },
  }, doneCallback, retrySubscriptionFn);
};

export const downloadPolls = async (presentationId: string | null): Promise<void> => {
  const options = { download: true };
  const path = `presenter/${presentationId}/polls/export`;
  await protectedContentApiClient.getFile(path, options);
};